import styles from "./feed-back-form.module.css";

import { ArrowLeft2 } from "iconsax-react";
import { type ReactNode, useState } from "react";

import { Button, RichTextEditorComponent } from "@/components/inputs";
import { Input, Label } from "@/components/inputs/input";
import { extractUserData } from "@/libs";
import type { DefaultConfiguration } from "@/types";
import { Group, Text } from "@mantine/core";
import { useForm } from "@mantine/form";

interface Props {
  apiKey?: string;
  apiURL?: string;
  authStatus: DefaultConfiguration["authStatus"];
  headers?: Record<string, string>;
  id?: null | number;
  onBack: () => void;
  supportGuide?: DefaultConfiguration["supportGuide"];
}

/**
 *
 * @summary
 *  * apiKey -> This is an authorization header.
 * 
 * e.g. import.meta.env.VITE_FEEDBACK_KEY.

 *  * apiURL -> This is the base url of the server.

 * e.g. import.meta.env.VITE_FEEDBACK_URL
 *
 */
export const FeedBackForm = ({
  apiKey,
  apiURL,
  authStatus,
  headers,
  onBack,
  supportGuide,
}: Props) => {
  const userData = extractUserData(authStatus);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const form = useForm({
    initialValues: {
      email: userData?.email ?? "",
      first_name: userData?.name ?? "",
      last_name: "",
      message: "",
      rating: "",
      subject: "",
    },
    validate: {
      email: (value) =>
        /^\S+@\S+$/.test(value.trim()) ? null : "Invalid email",
      first_name: (value) => (!value.trim() ? "First Name is required" : null),
      message: (value) => (!value.trim() ? "Message is required" : null),
      subject: (value) => (!value.trim() ? "Subject is required" : null),
    },
  });
  const isSelected = (rating: string) => form.values.rating === rating;

  const onRate = (rating: string) => () =>
    form.setFieldValue("rating", isSelected(rating) ? "" : rating);

  const handleSubmit = async (values: typeof form.values) => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await fetch(`${apiURL}`, {
        body: JSON.stringify({
          email: values.email,
          message: values.message,
          name: `${values.first_name} ${values.last_name}`.trim(),
          subject: values.subject,
          system: supportGuide?.system,
          topicId: 17,
        }),
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": `${apiKey}`,
          ...headers,
        },
        method: "POST",
      });

      if (response.ok) {
        setSuccess("Your issue has been reported successfully!");
        form.reset();
        setTimeout(() => {
          setSuccess("");
        }, 3000);
      } else {
        throw new Error(`Failed with status code ${response.status}`);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to report the issue. Please try again.");
      setTimeout(() => {
        setError("");
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <ArrowLeft2
        color="#292D32"
        size={35}
        className={styles.backArrow}
        onClick={onBack}
      />
      <Text className={styles.title} fw="bold">
        Feedback Form
      </Text>
      <Text className={styles.subtitle} fz="xs">
        {userData?.name} Kindly fill the feedback form as honestly as possible
        so we can understand how to serve you better.
      </Text>

      <form className={styles.formGrid} onSubmit={form.onSubmit(handleSubmit)}>
        <div className={styles.content}>
          {!userData?.name && (
            <Group>
              <Label required>First Name</Label>
              <Input
                type="text"
                placeholder="Enter first name"
                className={styles.subtitle}
                {...form.getInputProps("first_name")}
              />
            </Group>
          )}

          {form.errors.first_name && (
            <Text c="red" fz="xs">
              {form.errors.first_name}
            </Text>
          )}

          {!userData?.name && (
            <Group>
              <Label required>Last Name</Label>
              <Input
                type="text"
                placeholder="Enter last name"
                className={styles.subtitle}
                {...form.getInputProps("last_name")}
              />
            </Group>
          )}

          {form.errors.last_name && (
            <Text c="red" fz="xs">
              {form.errors.last_name}
            </Text>
          )}

          {!userData?.email && (
            <Group>
              <Label required>Email Address</Label>
              <Input
                type="email"
                placeholder="Enter email address"
                className={styles.subtitle}
                {...form.getInputProps("email")}
              />
            </Group>
          )}

          {form.errors.email && (
            <Text c="red" fz="xs">
              {form.errors.email}
            </Text>
          )}

          <Group>
            <Label required>Subject</Label>

            <Input
              type="text"
              placeholder="Enter the subject of your feedback"
              className={styles.subtitle}
              {...form.getInputProps("subject")}
            />
          </Group>
          {form.errors.subject && (
            <Text c="red" fz="xs">
              {form.errors.subject}
            </Text>
          )}

          <Group>
            <Label required>Message</Label>
            <RichTextEditorComponent
              name="message"
              onChange={(value) => form.setFieldValue("message", value)}
              placeholder="Enter your feedback message"
              value={form.values.message}
            />
          </Group>
          {form.errors.message && (
            <Text c="red" fz="xs">
              {form.errors.message}
            </Text>
          )}

          <div className={styles.reaction}>
            <Reaction
              selected={isSelected("Very Bad")}
              onReact={onRate("Very Bad")}
            >
              &#x1F621;
            </Reaction>
            <Reaction onReact={onRate("Bad")} selected={isSelected("Bad")}>
              &#x1F614;
            </Reaction>
            <Reaction
              onReact={onRate("Neutral")}
              selected={isSelected("Neutral")}
            >
              &#x1F611;
            </Reaction>
            <Reaction onReact={onRate("Good")} selected={isSelected("Good")}>
              &#x1F642;
            </Reaction>
            <Reaction
              onReact={onRate("Excellent")}
              selected={isSelected("Excellent")}
            >
              &#x1F970;
            </Reaction>
          </div>

          <div className={styles.actions}>
            <Button
              className={`${styles.button} ${styles.buttonGray}`}
              type="button"
              onClick={() => {
                form.reset();
                setError("");
                setSuccess("");
              }}
            >
              Cancel
            </Button>
            <Button
              className={`${styles.button} ${styles.buttonRed}`}
              type="submit"
              disabled={loading}
            >
              {loading ? "Sending..." : "Send"}
            </Button>
          </div>
          <div className={styles.feedbackStatus}>
            {error && (
              <Text c="red" py={8}>
                {error}
              </Text>
            )}
            {success && (
              <Text c="green" py={8}>
                {success}
              </Text>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

function Reaction({
  children,
  onReact,
  selected,
}: {
  children?: ReactNode;
  onReact: () => void;
  selected: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onReact}
      aria-selected={selected}
      className={styles.emoji}
    >
      {children}
    </button>
  );
}
